import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import API_BASE from '../apiConfig';
import BackButton from '../components/BackButton';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ResetPasswordPage() {
  const q = useQuery();
  const token = q.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const minLen = 8;
  const match = password && confirm && password === confirm;
  const strongEnough = password.length >= minLen;
  const canSubmit = token && strongEnough && match;

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (!canSubmit) return;
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      setDone(true);
      setPassword('');
      setConfirm('');
    } catch (e) {
      setErr(e.message);
    }
  }

  if (!token) {
    return (
      <div className="auth-container">
        <BackButton label="Tillbaka" />
        <div
          style={{
            padding: 24,
            borderRadius: 14,
            border: '1px solid #e5e7eb',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
          }}
        >
          <h2 style={{ marginTop: 0, marginBottom: 6 }}>Ogiltig länk</h2>
          <p style={{ color: '#6b7280', marginTop: 0 }}>
            Token saknas eller är ogiltig. Be om en ny återställningslänk.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <BackButton label="Tillbaka" />
      <div
        style={{
          padding: 24,
          borderRadius: 14,
          border: '1px solid #e5e7eb',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <div
            aria-hidden
            style={{
              width: 36, height: 36, borderRadius: 999,
              background: '#f5f3ff', display: 'grid', placeItems: 'center',
              fontWeight: 600, color: '#6d28d9'
            }}
          >●</div>
          <h2 style={{ margin: 0, fontSize: 20, lineHeight: '28px' }}>Återställ lösenord</h2>
        </div>
        <p style={{ margin: '2px 0 16px 48px', color: '#6b7280', fontSize: 14 }}>
          Välj ett nytt lösenord för ditt konto.
        </p>

        {done ? (
          <div
            role="status"
            style={{
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              color: '#065f46',
              borderRadius: 10,
              padding: 12
            }}
          >
            <strong style={{ display: 'block' }}>Klart!</strong>
            <span>Lösenordet är uppdaterat. Du kan nu logga in.</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gap: 6 }}>
              <label htmlFor="pw" style={{ fontWeight: 500 }}>Nytt lösenord</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  id="pw"
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  minLength={minLen}
                  required
                  autoComplete="new-password"
                  style={{ height: 40, borderRadius: 10, flex: 1 }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShow(s => !s)}
                  aria-label={show ? 'Dölj lösenord' : 'Visa lösenord'}
                  style={{ height: 36, borderRadius: 8, padding: '0 12px' }}
                >
                  {show ? 'Dölj' : 'Visa'}
                </button>
              </div>
              <div
                style={{
                  color: strongEnough ? '#059669' : '#6b7280',
                  fontSize: 12
                }}
              >
                Minst {minLen} tecken.
              </div>
            </div>

            <div style={{ display: 'grid', gap: 6 }}>
              <label htmlFor="pw2" style={{ fontWeight: 500 }}>Bekräfta lösenord</label>
              <input
                id="pw2"
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                minLength={minLen}
                required
                autoComplete="new-password"
                style={{ height: 40, borderRadius: 10 }}
              />
              {!!confirm && (
                <div
                  style={{
                    color: match ? '#059669' : '#ef4444',
                    fontSize: 12
                  }}
                >
                  {match ? 'Matchar' : 'Matchar inte'}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                className="btn-primary"
                disabled={!canSubmit}
                style={{ height: 36, borderRadius: 8, padding: '0 14px' }}
              >
                Spara nytt lösen
              </button>
            </div>

            {err && (
              <div
                role="alert"
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',   // ✅ fixed line
                  color: '#b91c1c',
                  borderRadius: 10,
                  padding: 10
                }}
              >
                {err}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
